import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertCard = ({ 
  alert, 
  onAcknowledge, 
  onInvestigate, 
  onResolve,
  className = "" 
}) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bgColor: 'bg-error/10',
          borderColor: 'border-error',
          textColor: 'text-error',
          icon: 'AlertTriangle',
          iconBg: 'bg-error/20'
        };
      case 'high':
        return {
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning',
          textColor: 'text-warning',
          icon: 'AlertCircle',
          iconBg: 'bg-warning/20'
        };
      case 'medium':
        return {
          bgColor: 'bg-accent/10',
          borderColor: 'border-accent',
          textColor: 'text-accent',
          icon: 'Info',
          iconBg: 'bg-accent/20'
        };
      case 'low':
        return {
          bgColor: 'bg-success/10',
          borderColor: 'border-success',
          textColor: 'text-success',
          icon: 'CheckCircle',
          iconBg: 'bg-success/20'
        };
      default:
        return {
          bgColor: 'bg-muted/10',
          borderColor: 'border-muted',
          textColor: 'text-muted-foreground',
          icon: 'Bell',
          iconBg: 'bg-muted/20'
        };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return { color: 'text-error', bg: 'bg-error/20', label: 'Active' };
      case 'acknowledged':
        return { color: 'text-warning', bg: 'bg-warning/20', label: 'Acknowledged' };
      case 'investigating':
        return { color: 'text-accent', bg: 'bg-accent/20', label: 'Investigating' };
      case 'resolved':
        return { color: 'text-success', bg: 'bg-success/20', label: 'Resolved' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted/20', label: 'Unknown' };
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diff = now - alertTime;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const severityConfig = getSeverityConfig(alert?.severity);
  const statusConfig = getStatusConfig(alert?.status);

  return (
    <div className={`
      ${severityConfig?.bgColor} ${severityConfig?.borderColor} 
      border-l-4 rounded-lg security-shadow-card p-4 security-transition
      hover:shadow-lg ${className}
    `}>
      <div className="flex items-start space-x-4">
        {/* Severity Icon */}
        <div className={`
          flex-shrink-0 w-10 h-10 rounded-full ${severityConfig?.iconBg} 
          flex items-center justify-center
          ${alert?.severity === 'critical' ? 'security-pulse' : ''}
        `}>
          <Icon 
            name={severityConfig?.icon} 
            size={20} 
            className={severityConfig?.textColor}
          />
        </div>

        {/* Alert Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground mb-1">
                {alert?.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {alert?.description}
              </p>
            </div>
            
            {/* Status Badge */}
            <span className={`
              px-2 py-1 rounded-full text-xs font-medium
              ${statusConfig?.bg} ${statusConfig?.color}
              flex-shrink-0 ml-2
            `}>
              {statusConfig?.label}
            </span>
          </div>

          {/* Alert Details */}
          <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-3">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={12} />
              <span>{alert?.device}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={12} />
              <span>{formatTimestamp(alert?.timestamp)}</span>
            </div>
            {alert?.userId && (
              <div className="flex items-center space-x-1">
                <Icon name="User" size={12} />
                <span>User: {alert?.userId}</span>
              </div>
            )}
          </div>

          {/* Behavioral Analysis Flag */}
          {alert?.behavioralFlag && (
            <div className="flex items-center space-x-2 mb-3 p-2 bg-warning/10 border border-warning/20 rounded-md">
              <Icon name="Brain" size={14} className="text-warning" />
              <span className="text-xs text-warning font-medium">
                Behavioral Anomaly: {alert?.behavioralFlag}
              </span>
            </div>
          )}

          {/* Action Buttons */}
          {alert?.status === 'active' && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAcknowledge(alert?.id)}
                iconName="Check"
                className="text-xs"
              >
                Acknowledge
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onInvestigate(alert?.id)}
                iconName="Search"
                className="text-xs"
              >
                Investigate
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => onResolve(alert?.id)}
                iconName="CheckCircle"
                className="text-xs"
              >
                Resolve
              </Button>
            </div>
          )}

          {alert?.status === 'acknowledged' && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onInvestigate(alert?.id)}
                iconName="Search"
                className="text-xs"
              >
                Investigate
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => onResolve(alert?.id)}
                iconName="CheckCircle"
                className="text-xs"
              >
                Resolve
              </Button>
            </div>
          )}

          {alert?.status === 'investigating' && (
            <div className="flex items-center space-x-2">
              <Button
                variant="success"
                size="sm"
                onClick={() => onResolve(alert?.id)}
                iconName="CheckCircle"
                className="text-xs"
              >
                Resolve
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertCard;